import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Line } from "react-chartjs-2";
import {
  Chart,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend,
} from "chart.js";
import axiosInstance from "../../services/api/axios";
import { useUser } from "../../context/UserContext";
import OverallSales from "./OverallSales";
import SalesPerUnit from "./SalesPerUnit";
import IncomeReports from "./IncomeReports";
import moment from "moment";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import {
  BadgeIndianRupee,
  Calendar,
  Home,
  ListChecks,
  Wallet,
  TrendingUp,
  ArrowLeft,
  Users,
} from "lucide-react";
import Skeleton from "react-loading-skeleton";

Chart.register(CategoryScale, LinearScale, PointElement, LineElement, Filler, Tooltip, Legend);

/* ---------- formatting helpers ---------- */
const fmtINR = (n) => {
  const v = Number(n || 0);
  if (v >= 1e7) return `₹${(v / 1e7).toFixed(2)} Cr`;
  if (v >= 1e5) return `₹${(v / 1e5).toFixed(2)} L`;
  if (v >= 1e3) return `₹${(v / 1e3).toFixed(1)} K`;
  return `₹${Math.round(v)}`;
};
const fmtInt = (n) => Number(n || 0).toLocaleString("en-IN");
const fmtPct = (n) => {
  const v = Number(n || 0);
  return (Math.trunc(v * 100) / 100).toFixed(2);
};
const monthLabel = (yyyymm) => {
  if (!yyyymm) return "";
  const [y, m] = yyyymm.split("-");
  return new Date(Number(y), Number(m) - 1, 1).toLocaleString("en-IN", { month: "short" });
};

const TABS = [
  { label: "Overall sales", value: "overallSales", icon: BadgeIndianRupee },
  { label: "Per unit", value: "salesPerUnit", icon: Home },
  { label: "Income reports", value: "incomeReports", icon: Wallet },
];

const PERIODS = [
  { value: "custom", label: "Custom range" },
  { value: "monthly", label: "Last month" },
  { value: "quarterly", label: "Last quarter" },
  { value: "yearly", label: "Last year" },
];

const KpiTile = ({ icon: Icon, label, value, subtitle, accent = "slate" }) => {
  const accents = {
    flamingo: "bg-flamingo-50 text-flamingo-600",
    slate: "bg-slate-100 text-slate-700",
    emerald: "bg-emerald-50 text-emerald-600",
    indigo: "bg-indigo-50 text-indigo-600",
  };
  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 flex flex-col gap-3 hover:shadow-md transition-shadow">
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${accents[accent]}`}>
        <Icon size={20} />
      </div>
      <div>
        <p className="text-xs uppercase tracking-wide text-slate-500 font-medium">{label}</p>
        <p className="text-2xl font-bold text-slate-900 mt-1 tabular-nums">{value}</p>
        {subtitle && <p className="text-xs text-slate-500 mt-1">{subtitle}</p>}
      </div>
    </div>
  );
};

function SalesReport() {
  const { userInfo } = useUser();
  const location = useLocation();
  const navigate = useNavigate();
  const customerId = userInfo?.loginIdentifierDetails?.customer?.customerId;

  const getInitialTab = useCallback(() => {
    const hash = location.hash.replace("#", "");
    return hash || "overallSales";
  }, [location.hash]);

  const [activeTab, setActiveTab] = useState(getInitialTab());
  useEffect(() => setActiveTab(getInitialTab()), [getInitialTab]);
  useEffect(() => {
    navigate(`#${activeTab}`, { replace: true });
  }, [activeTab, navigate]);

  /* ---------- filters ---------- */
  const [startDate, setStartDate] = useState(moment().subtract(3, "months").toDate());
  const [endDate, setEndDate] = useState(moment().toDate());
  const [periodFilter, setPeriodFilter] = useState("custom");
  const [selectedProjectId, setSelectedProjectId] = useState("");
  const [projectOptions, setProjectOptions] = useState([]);

  useEffect(() => {
    if (periodFilter === "custom") return;
    let s;
    let e;
    if (periodFilter === "monthly") {
      s = moment().subtract(1, "months").startOf("month").toDate();
      e = moment().subtract(1, "months").endOf("month").toDate();
    } else if (periodFilter === "quarterly") {
      s = moment().subtract(1, "quarters").startOf("quarter").toDate();
      e = moment().subtract(1, "quarters").endOf("quarter").toDate();
    } else if (periodFilter === "yearly") {
      s = moment().subtract(1, "years").startOf("year").toDate();
      e = moment().subtract(1, "years").endOf("year").toDate();
    }
    setStartDate(s);
    setEndDate(e);
  }, [periodFilter]);

  /* ---------- data fetching ---------- */
  const [summary, setSummary] = useState(null);
  const [salesReportData, setSalesReportData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Project picker — shared with the dashboard.
  useEffect(() => {
    let mounted = true;
    axiosInstance
      .get("/dashboard/projects")
      .then((res) => {
        if (mounted) setProjectOptions(Array.isArray(res) ? res : res?.items || []);
      })
      .catch(() => {
        if (mounted) setProjectOptions([]);
      });
    return () => {
      mounted = false;
    };
  }, []);

  // Composite summary + the existing detail payload, in parallel.
  useEffect(() => {
    if (!customerId) {
      setError("Customer ID not available.");
      setLoading(false);
      return;
    }
    let mounted = true;
    setLoading(true);
    setError(null);
    const startStr = moment(startDate).format("YYYY-MM-DD");
    const endStr = moment(endDate).format("YYYY-MM-DD");
    const summaryParams = { startDate: startStr, endDate: endStr };
    if (selectedProjectId) summaryParams.projectId = selectedProjectId;

    Promise.all([
      axiosInstance.get("/reports/sales-summary", { params: summaryParams }),
      axiosInstance.get(
        `/customer/sales/report?customer_id=${customerId}&start_date=${startStr}&end_date=${endStr}${
          selectedProjectId ? `&project_id=${selectedProjectId}` : ""
        }`
      ),
    ])
      .then(([sum, detail]) => {
        if (!mounted) return;
        setSummary(sum);
        setSalesReportData(detail);
      })
      .catch((err) => {
        if (mounted) setError(err?.response?.data?.detail || err.message || "Failed to load");
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, [customerId, startDate, endDate, selectedProjectId]);

  /* ---------- derived ---------- */
  const trendChart = useMemo(() => {
    const points = summary?.trend || [];
    return {
      data: {
        labels: points.map((p) => monthLabel(p.month)),
        datasets: [
          {
            label: "Revenue",
            data: points.map((p) => Number(p.revenue || 0)),
            borderColor: "#f97316",
            backgroundColor: "rgba(249, 115, 22, 0.12)",
            tension: 0.35,
            fill: true,
            pointRadius: 3,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false },
          tooltip: { callbacks: { label: (ctx) => fmtINR(ctx.parsed.y) } },
        },
        scales: {
          x: { grid: { display: false }, ticks: { font: { size: 11 } } },
          y: {
            beginAtZero: true,
            ticks: { callback: (v) => fmtINR(v), font: { size: 11 } },
            grid: { color: "#f1f5f9" },
          },
        },
      },
    };
  }, [summary]);

  const selectedProjectLabel =
    selectedProjectId === ""
      ? "All projects"
      : projectOptions.find((p) => String(p.projectId) === String(selectedProjectId))
          ?.projectName || "Project";

  const dateRangeLabel = `${moment(startDate).format("DD MMM YYYY")} – ${moment(endDate).format(
    "DD MMM YYYY"
  )}`;

  /* ---------- tab body ---------- */
  const renderActiveTab = () => {
    if (loading) {
      return (
        <div className="space-y-3">
          <Skeleton height={28} width="40%" />
          <Skeleton height={200} />
          <Skeleton height={120} />
        </div>
      );
    }
    if (error) {
      return (
        <div className="rounded-2xl border border-rose-200 bg-rose-50 text-rose-700 px-4 py-3 text-sm">
          {error}
        </div>
      );
    }
    if (!salesReportData) {
      return <div className="text-sm text-slate-500">No data available.</div>;
    }
    switch (activeTab) {
      case "overallSales":
        return <OverallSales data={salesReportData?.customerSales || []} />;
      case "salesPerUnit":
        return <SalesPerUnit data={salesReportData?.customerSales || []} />;
      case "incomeReports":
        return (
          <IncomeReports
            scheduledData={salesReportData?.SCHEDULED || []}
            slabwiseData={salesReportData?.SLAB_WISE || []}
            hybridData={salesReportData?.HYBRID || []}
            projectDetails={{}}
            startDate={startDate}
            endDate={endDate}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 flex flex-col gap-6 min-h-[calc(100vh-4rem)] bg-slate-50/40">
      {/* Hero — slate gradient + project picker + period & date filters */}
      <div className="rounded-2xl shadow-sm bg-gradient-to-r from-slate-800 via-slate-700 to-slate-600 p-5 lg:p-6 text-white">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <p className="text-xs uppercase tracking-wider text-slate-300 flex items-center gap-1.5">
              <ListChecks size={12} /> Reports
            </p>
            <h1 className="text-xl sm:text-2xl font-semibold tracking-tight mt-1">Sales report</h1>
            <p className="text-xs text-slate-300 mt-1 flex items-center gap-1.5">
              <Calendar size={12} />
              {dateRangeLabel}
              <span className="mx-1.5 text-slate-500">•</span>
              {selectedProjectLabel}
            </p>
          </div>
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-1 text-xs font-medium text-slate-200 hover:text-white self-start lg:self-end"
          >
            <ArrowLeft size={14} /> Back
          </button>
        </div>

        <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div className="flex flex-col gap-1.5">
            <label className="text-[11px] uppercase tracking-wider text-slate-300">Project</label>
            <select
              value={selectedProjectId}
              onChange={(e) => setSelectedProjectId(e.target.value)}
              className="px-3 py-2 rounded-lg bg-white/10 text-white text-sm border border-white/20 focus:bg-white/20 focus:border-white/40 focus:outline-none"
            >
              <option value="" className="text-slate-900">
                All projects
              </option>
              {projectOptions.map((p) => (
                <option key={p.projectId} value={p.projectId} className="text-slate-900">
                  {p.projectName}
                </option>
              ))}
            </select>
          </div>
          <div className="flex flex-col gap-1.5">
            <label className="text-[11px] uppercase tracking-wider text-slate-300">Period</label>
            <select
              value={periodFilter}
              onChange={(e) => setPeriodFilter(e.target.value)}
              className="px-3 py-2 rounded-lg bg-white/10 text-white text-sm border border-white/20 focus:bg-white/20 focus:border-white/40 focus:outline-none"
            >
              {PERIODS.map((p) => (
                <option key={p.value} value={p.value} className="text-slate-900">
                  {p.label}
                </option>
              ))}
            </select>
          </div>
          <div className="flex flex-col gap-1.5">
            <label className="text-[11px] uppercase tracking-wider text-slate-300">From</label>
            <div className="relative">
              <DatePicker
                selected={startDate}
                onChange={(d) => {
                  setStartDate(d);
                  setPeriodFilter("custom");
                }}
                dateFormat="yyyy-MM-dd"
                className="px-3 py-2 pl-8 rounded-lg bg-white/10 text-white text-sm border border-white/20 focus:bg-white/20 focus:border-white/40 focus:outline-none w-full"
              />
              <Calendar
                size={14}
                className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-300 pointer-events-none"
              />
            </div>
          </div>
          <div className="flex flex-col gap-1.5">
            <label className="text-[11px] uppercase tracking-wider text-slate-300">To</label>
            <div className="relative">
              <DatePicker
                selected={endDate}
                onChange={(d) => {
                  setEndDate(d);
                  setPeriodFilter("custom");
                }}
                dateFormat="yyyy-MM-dd"
                className="px-3 py-2 pl-8 rounded-lg bg-white/10 text-white text-sm border border-white/20 focus:bg-white/20 focus:border-white/40 focus:outline-none w-full"
              />
              <Calendar
                size={14}
                className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-300 pointer-events-none"
              />
            </div>
          </div>
        </div>
      </div>

      {/* KPI tiles */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {loading ? (
          Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 h-32">
              <Skeleton circle height={40} width={40} />
              <Skeleton height={12} width="70%" className="mt-3" />
              <Skeleton height={28} width="50%" className="mt-2" />
            </div>
          ))
        ) : (
          <>
            <KpiTile
              icon={BadgeIndianRupee}
              label="Total sales"
              value={fmtInt(summary?.totalSales)}
              subtitle="Bookings in period"
              accent="flamingo"
            />
            <KpiTile
              icon={Wallet}
              label="Total revenue"
              value={fmtINR(summary?.totalRevenue)}
              subtitle={`Avg ${fmtINR(summary?.averageSalePrice)} / sale`}
              accent="emerald"
            />
            <KpiTile
              icon={TrendingUp}
              label="Loan-funded share"
              value={`${fmtPct(summary?.loanSharePercent)}%`}
              subtitle="of bookings used a loan"
              accent="indigo"
            />
            <KpiTile
              icon={Users}
              label="Top performer"
              value={summary?.topSalespeople?.[0]?.name || "—"}
              subtitle={
                summary?.topSalespeople?.[0]
                  ? `${fmtInt(summary.topSalespeople[0].salesCount)} sales · ${fmtINR(
                      summary.topSalespeople[0].revenue
                    )}`
                  : "No data"
              }
              accent="slate"
            />
          </>
        )}
      </div>

      {/* Trend chart + source split */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 bg-white rounded-2xl border border-slate-200 shadow-sm">
          <div className="p-5 pb-2">
            <h3 className="text-base font-semibold text-slate-900">Revenue trend</h3>
            <p className="text-xs text-slate-500">Monthly revenue across the date range</p>
          </div>
          <div className="p-5 pt-2 h-64">
            {loading ? (
              <Skeleton height={220} />
            ) : (summary?.trend || []).length === 0 ? (
              <div className="h-full flex items-center justify-center text-sm text-slate-500">
                No revenue in this range
              </div>
            ) : (
              <Line data={trendChart.data} options={trendChart.options} />
            )}
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm">
          <div className="p-5 pb-2">
            <h3 className="text-base font-semibold text-slate-900">By source</h3>
            <p className="text-xs text-slate-500">Where sales came from</p>
          </div>
          <div className="p-5 pt-2">
            {loading ? (
              <Skeleton height={180} />
            ) : (summary?.bySource || []).length === 0 ? (
              <div className="text-sm text-slate-500">No source data</div>
            ) : (
              <ul className="space-y-3">
                {summary.bySource.map((s) => {
                  const share =
                    summary.totalSales > 0 ? (s.salesCount / summary.totalSales) * 100 : 0;
                  return (
                    <li key={s.source}>
                      <div className="flex items-center justify-between text-xs text-slate-600 mb-1">
                        <span className="font-medium text-slate-900">
                          {s.source.replaceAll("_", " ")}
                        </span>
                        <span className="tabular-nums">
                          {fmtInt(s.salesCount)} · {fmtINR(s.revenue)}
                        </span>
                      </div>
                      <div className="w-full h-1.5 bg-slate-100 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-flamingo-400 to-flamingo-600"
                          style={{ width: `${Math.min(100, share)}%` }}
                        />
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      </div>

      {/* Detail tabs */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-1.5 inline-flex gap-1 self-start flex-wrap">
        {TABS.map(({ value, label, icon: Icon }) => (
          <button
            key={value}
            type="button"
            onClick={() => setActiveTab(value)}
            className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-medium transition-colors ${
              activeTab === value
                ? "bg-gradient-to-r from-flamingo-500 to-flamingo-600 text-white shadow-sm"
                : "text-slate-600 hover:text-slate-900 hover:bg-slate-50"
            }`}
          >
            <Icon size={14} />
            {label}
          </button>
        ))}
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm">
        <div className="p-4 sm:p-5">{renderActiveTab()}</div>
      </div>
    </div>
  );
}

export default SalesReport;
